import Modal from '@components/v2/ui/Modal/Modal';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Divider, FormControlLabel, InputAdornment, Radio, RadioGroup, Stack } from '@mui/material';
import MoneyTextField from '../../../../components/FormInputs/MoneyTextField';
import NumberTextField from '../../../../components/FormInputs/NumberTextField';
import useMoney from '../../../../hooks/useMoney';
import LangEnum from '../../../../lang/LangEnum';
import { PaymentValueType } from '../../../payment-method/payment-method.types';
import { useTranslation } from '../../../translation/TranslationContext';
import OrderSharePaymentModal from './components/OrderSharePaymentModal/OrderSharePaymentModal';
import { OrderModalPaymentType } from './enums/OrderModalPaymentType.enum';
import { OrderPaymentModalView } from './enums/OrderModalView.enum';
import { useOrderPaymentModal } from './hooks/useOrderPaymentModal';

interface OrderPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderCode: string;
  defaultView?: OrderPaymentModalView;
}

const OrderPaymentModal = ({
  isOpen,
  onClose,
  orderId,
  orderCode,
  defaultView,
}: OrderPaymentModalProps) => {
  const { translate } = useTranslation();
  const { formatMoney, currencySymbol } = useMoney();

  const {
    order,
    error,
    value,
    isLoading,
    isInputDisabled,
    paymentType,
    minimumPaymentPercentage,
    orderTotalValue,
    handleDefaultState,
    minimumValue,
    onChangePaymentType,
    onChangeValue,
    onChangeView,
    handleGeneratePaymentLink,
    handleSharePaymentLink,
    isSubmitting,
    view,
    hasActivePaymentLink,
    mustShowCancelPaymentButton,
    handleCancelPaymentLink,
  } = useOrderPaymentModal({ isOpen, orderId, onClose, defaultView });

  if (view.current === OrderPaymentModalView.CANCEL_LINK_CONFIRMATION) {
    return (
      <Modal
        isOpen={isOpen}
        size="standard"
        header={{ title: LangEnum.PAYMENT_LINK_CANCEL_CONFIRMATION_TITLE }}
        onClose={onClose}
        onExited={() => onChangeView({ current: OrderPaymentModalView.PAYMENT })}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_CONFIRM_LABEL,
            onClick: handleCancelPaymentLink,
            isDisabled: isSubmitting,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_GO_BACK,
            onClick: () => onChangeView({ current: OrderPaymentModalView.PAYMENT }),
            isDisabled: isSubmitting,
          },
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {translate(LangEnum.PAYMENT_LINK_CANCEL_CONFIRMATION_DESCRIPTION, { orderCode })}
        </Typography>
      </Modal>
    );
  }

  if (order && view.current === OrderPaymentModalView.SHARE_LINK && view.paymentUrl) {
    return (
      <OrderSharePaymentModal
        isOpen={isOpen}
        onClose={onClose}
        orderId={orderId}
        orderCode={orderCode}
        paymentUrl={view.paymentUrl}
        onClickBack={() => onChangeView({ current: OrderPaymentModalView.PAYMENT })}
        onTransitionExited={() => onChangeView({ current: OrderPaymentModalView.PAYMENT })}
        customerId={order.customerId}
      />
    );
  }

  const isDisabled = !!error || value.monetaryValue <= 0 || isSubmitting;

  const tooltipContent = isInputDisabled ? (
    <Typography variant="body2" color="white">
      {hasActivePaymentLink
        ? translate(LangEnum.ORDER_PAYMENT_DISABLED_ACTIVE_LINK)
        : translate(LangEnum.ORDER_PAYMENT_DISABLED_FULL_VALUE_ONLY)}
    </Typography>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{ title: `${translate(LangEnum.ORDER_PAYMENT_TITLE)} ${orderCode}` }}
      onClose={onClose}
      onExited={() => onChangeView({ current: OrderPaymentModalView.PAYMENT })}
      footer={{
        primaryAction: {
          label: LangEnum.ORDER_PAYMENT_GO_TO_PAYMENT,
          isDisabled,
          isLoading: isSubmitting,
          onClick: handleGeneratePaymentLink,
        },
        secondaryAction: {
          label: LangEnum.ORDER_PAYMENT_SHARE_LINK,
          isDisabled,
          onClick: handleSharePaymentLink,
        },
        tertiaryAction: mustShowCancelPaymentButton
          ? {
              label: LangEnum.PAYMENT_LINK_CANCEL_BUTTON_LABEL,
              onClick: () =>
                onChangeView({ current: OrderPaymentModalView.CANCEL_LINK_CONFIRMATION }),
            }
          : undefined,
      }}
    >
      {!isLoading && (
        <Stack
          spacing={3}
          sx={{
            marginTop: '-8px',
          }}
        >
          <Tooltip TooltipContent={tooltipContent}>
            <Stack spacing={3}>
              <RadioGroup
                defaultValue={OrderModalPaymentType.VALUE}
                row
                value={paymentType}
                onChange={(e) => {
                  const newPaymentType = e.target.value as OrderModalPaymentType;
                  onChangePaymentType(newPaymentType);
                  handleDefaultState({
                    minimumPaymentPercentage,
                    orderTotalValue,
                    paymentType: newPaymentType,
                    paymentValueType: order?.paymentValueType ?? PaymentValueType.FULL_VALUE,
                  });
                }}
              >
                <FormControlLabel
                  disabled={isInputDisabled}
                  sx={{ textTransform: 'none' }}
                  value={OrderModalPaymentType.VALUE}
                  control={<Radio color="primary" />}
                  label={translate(LangEnum.GLOBAL_FIXED_VALUE, { currencySymbol })}
                />

                <FormControlLabel
                  disabled={isInputDisabled}
                  value={OrderModalPaymentType.PERCENTAGE}
                  control={<Radio color="primary" />}
                  label={translate(LangEnum.COUPONS_FORM_TYPE_PERCENT)}
                />
              </RadioGroup>

              <Stack spacing={1}>
                {paymentType === OrderModalPaymentType.VALUE && (
                  <MoneyTextField
                    disabled={isInputDisabled}
                    label={`${translate(LangEnum.ORDER_PAYMENT_VALUE_TO_PAY)} *`}
                    value={value.inputValue}
                    error={!!error}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = Math.max(0, Math.min(+e.target.value, orderTotalValue));
                      onChangeValue({ inputValue: newValue, monetaryValue: newValue });
                    }}
                  />
                )}
                {paymentType === OrderModalPaymentType.PERCENTAGE && (
                  <NumberTextField
                    disabled={isInputDisabled}
                    label={`${translate(LangEnum.ORDER_PAYMENT_VALUE_TO_PAY)} *`}
                    value={value.inputValue}
                    error={!!error}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const percentage = Math.max(0, Math.min(+event.target.value, 100));
                      onChangeValue({
                        inputValue: percentage,
                        monetaryValue: (orderTotalValue * percentage) / 100,
                      });
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                )}
                {order?.paymentValueType === PaymentValueType.PARTIAL_VALUE && (
                  <Typography variant="body2" color={error ? 'error' : 'textSecondary'}>
                    {error
                      ? translate(LangEnum.ORDER_PAYMENT_VALUE_BELOW_MINIMUM)
                      : translate(LangEnum.PAYMENT_MINIMUM_VALUE)}
                    :{' '}
                    {paymentType === OrderModalPaymentType.VALUE
                      ? formatMoney(minimumValue)
                      : `${minimumPaymentPercentage}%`}
                  </Typography>
                )}
                {order?.paymentValueType === PaymentValueType.FULL_VALUE && (
                  <Typography variant="body2" color="grey.400">
                    {translate(LangEnum.ORDER_PAYMENT_FULL_PAYMENT_ONLY)}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Tooltip>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="overline" color="grey.600">
              {translate(LangEnum.GLOBAL_SUMMARY)}
            </Typography>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Typography>{translate(LangEnum.REPORT_HEADER_ORDER_TOTAL)}</Typography>
              <Typography>{formatMoney(orderTotalValue)}</Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Typography>{translate(LangEnum.ORDER_PAYMENT_VALUE_TO_PAY)}</Typography>
              <Typography>{formatMoney(value.monetaryValue)}</Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};

export default OrderPaymentModal;
